import { Alert, Button, DatePicker, Form, Switch, message } from 'antd'
import { Flex, Heading } from '@weareberyl/design-system'
import moment from 'moment'

import ActionModal, { ActionModalProps } from './ActionModal'
import { useUpdateUserLicenceExpiryDateMutation } from '~/gql/generated/graphql'

const layout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 17 },
}

const tailLayout = {
  wrapperCol: { offset: 7 },
}

type Props = ActionModalProps & { userId: string }
type FormValues = {
  expiryDate: moment.Moment
  verified: boolean
}

const LicenceExpiryDateModal = ({ visible, close, userId }: Props) => {
  const [form] = Form.useForm()
  const [setExpiryDate, { loading: saving, error }] =
    useUpdateUserLicenceExpiryDateMutation()

  const onFinish = async ({ expiryDate }: FormValues) => {
    try {
      await setExpiryDate({
        variables: {
          userId,
          expiryDate: expiryDate.format('YYYY-MM-DD'),
        },
      })
    } catch {
      message.error('Expiry date could not be updated')
      return
    }
    message.info('Expiry date updated')
    close()
  }

  return (
    <ActionModal visible={visible} close={close}>
      <Flex pt={4} px={6} style={{ gap: '20px' }}>
        <Heading variant="callout" pt={2} pb={4}>
          Update expiry date
        </Heading>
        {error && (
          <Alert
            type="error"
            message="Oh dear"
            description={error.message}
            showIcon
          />
        )}
        <Alert
          message="This is a workaround"
          type="warning"
          description={
            <p>
              Users should be able to authenticate via Persona in the app. In
              the event that they are unable to due to Persona not recognising
              the user's expiry date on their licence, set their expiry date
              here.
            </p>
          }
          showIcon
        />
        <Form
          {...layout}
          form={form}
          onFinish={onFinish}
          layout="horizontal"
          name="expiryDateForm"
          style={{ flex: 1 }}
        >
          <Form.Item
            name="expiryDate"
            label="Expiry Date"
            rules={[{ required: true, message: 'Missing expiry date' }]}
          >
            <DatePicker
              format="YYYY-MM-DD"
              disabledDate={current => current < moment().startOf('day')}
              data-testid="expiry-date"
            />
          </Form.Item>

          <Form.Item
            name="verified"
            label="Verified"
            valuePropName="checked"
            extra="I have checked in Persona that this expiry date is correct"
            rules={[
              {
                required: true,
                message: 'Please verify that the expiry date is correct',
              },
            ]}
          >
            <Switch />
          </Form.Item>

          <Form.Item {...tailLayout}>
            <Button type="primary" htmlType="submit" loading={saving}>
              Update expiry date
            </Button>
          </Form.Item>
        </Form>
      </Flex>
    </ActionModal>
  )
}

export default LicenceExpiryDateModal
